import { io, type Socket } from 'socket.io-client'
import type { ClientToServerEvents, ServerToClientEvents } from '@live-stream/shared'
import { loadResume, saveMembership, saveResume } from './resume.js'
import { readKey, writeKey, type KeyValueStore } from './storage.js'

/**
 * The typed Socket.IO connection, one per SDK client.
 *
 * The event maps are the ones the server implements, so a renamed event or
 * a changed payload is a compile error on both sides. See docs/EVENTS.md.
 *
 * Identity across reconnects: the peer id is NOT the socket id. The server
 * sends `session:hello` first on every connection, with the id this socket
 * answers to and a resume token; both go into session storage and back into
 * the next handshake, so a reconnect keeps the id, and with it every peer
 * connection the others hold for us.
 */
export type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>

export interface SocketManagerOptions {
  /** Origin of the server; empty for this page's origin. */
  baseUrl: string
  /** Where the resume credentials and the membership claim are kept. */
  storage: KeyValueStore | null
  /**
   * Where this browser's device id is kept: storage every tab shares and a reload
   * keeps (localStorage). Null: a new id per client, which only loses the
   * one-seat-per-browser rule (SocketData.deviceId).
   */
  deviceStorage?: KeyValueStore | null
}

const DEVICE_KEY = 'ls_device'

/**
 * A provider, called on EVERY connection attempt, not once: access tokens
 * expire in minutes and calls last longer, and a reconnect that presented
 * the token from join time would be turned away for no good reason.
 */
export type TokenProvider = () => Promise<string | null> | string | null

export class SocketManager {
  private socket: AppSocket | null = null
  /** The peer id of the CURRENT connection; null between a drop and the next hello. */
  private peerId: string | null = null
  private readonly waiters = new Set<(id: string) => void>()
  /**
   * Random per client and deliberately NOT stored: a duplicated tab copies
   * session storage, so the resume credentials alone cannot tell "this tab,
   * reconnecting" from "a copy of this tab". This can.
   */
  private readonly instance = randomId()
  /**
   * This browser, across tabs and page loads, and deliberately stored: the
   * server lets it hold one seat in a meeting, so the link opened again takes
   * the old seat over instead of adding a copy of the person.
   */
  private readonly device: string

  constructor(private readonly opts: SocketManagerOptions) {
    const kept = readKey(opts.deviceStorage ?? null, DEVICE_KEY)
    this.device = kept && /^[\w-]{8,64}$/.test(kept) ? kept : randomId()
    if (this.device !== kept) writeKey(opts.deviceStorage ?? null, DEVICE_KEY, this.device)
  }

  /** The connection, opened on first call with this token provider; later calls return it as is. */
  connect(token: TokenProvider): AppSocket {
    if (this.socket) return this.socket

    const options = {
      path: '/socket.io',
      transports: ['websocket'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5_000,
      // Identity is established in the handshake, once per connection, and
      // verified server-side. Nothing sent later can change who we are.
      auth: (cb: (data: object) => void) => {
        void Promise.resolve(token()).then((t) =>
          cb({
            token: t ?? null,
            resume: loadResume(this.opts.storage),
            instance: this.instance,
            device: this.device,
          }),
        )
      },
    }
    const sock: AppSocket = this.opts.baseUrl ? io(this.opts.baseUrl, options) : io(options)

    sock.on('session:hello', (msg) => {
      this.peerId = msg.peerId
      saveResume({ peerId: msg.peerId, token: msg.resumeToken }, this.opts.storage)
      for (const resolve of this.waiters) resolve(msg.peerId)
      this.waiters.clear()
    })
    sock.on('session:member', (msg) => saveMembership(msg, this.opts.storage))
    sock.on('disconnect', () => {
      // The next connection brings its own hello: usually the same id, but
      // not always, and nothing may act on the old one meanwhile.
      this.peerId = null
    })

    this.socket = sock
    return sock
  }

  /** The open connection, if any. */
  current(): AppSocket | null {
    return this.socket
  }

  /** The peer id of the current connection: at once if hello has arrived, else when it does. */
  waitForPeerId(): Promise<string> {
    if (this.peerId) return Promise.resolve(this.peerId)
    return new Promise((resolve) => this.waiters.add(resolve))
  }

  disconnect(): void {
    this.socket?.disconnect()
    this.socket = null
    this.peerId = null
    this.waiters.clear()
  }
}

/** The ping/pong round trip, in ms. */
export function measureRoundTrip(sock: AppSocket, timeoutMs = 5_000): Promise<number> {
  return new Promise((resolve, reject) => {
    const echo = Math.random().toString(36).slice(2)
    const startedAt = performance.now()

    const timer = setTimeout(() => {
      sock.off('pong', onPong)
      reject(new Error('Timed out waiting for the server'))
    }, timeoutMs)

    function onPong(msg: { at: number; echo: string }): void {
      if (msg.echo !== echo) return
      clearTimeout(timer)
      sock.off('pong', onPong)
      resolve(Math.round(performance.now() - startedAt))
    }

    sock.on('pong', onPong)
    sock.emit('ping', { echo })
  })
}

function randomId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}
